import React, {
  useContext,
  useCallback,
  useState,
  useRef,
  useEffect,
} from "react";
import _ from "lodash";
import styled, { useTheme } from "styled-components";
import { Flex } from "reflexbox";
import Button from "../UI/Button";
import { Heading } from "../UI/Text";
import UserContext from "../../context/user/UserContext";
import ConfiguratorContext from "../../context/configurator/ConfiguratorContext";
import { configure } from "../../requests/apiRequests";
import formatter from "../../modules/formatter";
import * as constants from "../../modules/constants";
import Loading from "../UI/Loading";
import { useParams } from "react-router-dom";

const PagesWrapper = styled(Flex)`
  flex: 3;
  justify-content: center;
  align-items: center;
  @media (max-width: 600px) {
    .hide {
      display: none;
    }
  }
`;

const Wrapper = styled(Flex)`
  width: 100%;
  margin-left: -1rem;
  flex-direction: column;
  max-width: 1168px;

  @media (min-width: 1200px) {
    width: calc(100% + 2rem);
    margin: 0 auto !important;
  }
`;

const BlurContainer = styled(Flex)`
  background-color: ${({ theme }) => theme.backgroundColor}47;
  backdrop-filter: blur(10px);
  position: sticky;
  top: 0;
  z-index: 100;
`;

const TopBar = (props) => {
  const theme = useTheme();
  const { activeUser, platform } = useContext(UserContext);
  const { productId } = useParams();

  const { requireHttps } = platform || {};
  const {
    api,
    setApi,
    currentPage,
    setCurrentPage,
    autoContinueMode,
    setACMode,
    configuratorMode,
  } = useContext(ConfiguratorContext);

  const { ConfiguredPrice, Pages = [] } = api?.d || {};
  const price = `$${formatter.currency(ConfiguredPrice)}`;
  const {
    setShowCancel,
    finishConfiguration,
    sessionId,
    setShowDetails,
    setShowCreateUser,
  } = props || {};

  const [running, setRunning] = useState(false);

  const continueClick = useCallback(
    async (_id) => {
      let lastScreenOption = {};
      const lastValue = api?.d?.Pages?.length - 1;
      const maxScreenOptions = api?.d?.Pages[lastValue].Screens.slice()
        .reverse()
        .find((s) => s.ScreenOptions.length > 0);

      const { ScreenOptions } = maxScreenOptions || {};
      const { length } = ScreenOptions || [];
      const { [length - 1]: _lastScreenOption } = ScreenOptions || {};
      lastScreenOption = _lastScreenOption;
      if (!lastScreenOption?.Value) {
        const element = document.getElementById(lastScreenOption.ID);
        lastScreenOption.Value = element?.value || "";
      }

      const id = document.activeElement.id;
      const value = document.activeElement.value;

      setACMode(autoContinueMode);
      setRunning(true);
      const res = await configure({
        sessionId,
        id,
        value,
        requireHttps,
        productId,
      });
      setApi(res.data);
      setRunning(false);

      setCurrentPage(res?.data?.d?.Pages?.length - 1);
    },
    [
      api,
      autoContinueMode,
      requireHttps,
      sessionId,
      setACMode,
      setApi,
      setCurrentPage,
      productId,
    ]
  );

  const [isFinishing, setFinishing] = useState(false);

  const onArrowClick = useCallback(
    (action) => {
      if (action === "next" && currentPage + 1 >= Pages?.length) return;
      if (action === "previous" && currentPage === 0) return;
      setCurrentPage(currentPage + (action === "next" ? 1 : -1));
    },
    [Pages, currentPage, setCurrentPage]
  );

  const finishClick = useCallback(async () => {
    if (!activeUser) setShowCreateUser(true);
    else {
      setFinishing(true);
      await finishConfiguration({ sessionId });
    }
  }, [activeUser, finishConfiguration, sessionId, setShowCreateUser]);

  const listener = useCallback(
    (e) => {
      // Trigger the button element with a click
      if (e.key === "Enter") {
        e.preventDefault();
        if (api?.d?.IsExecutionComplete && finishRef.current) {
          setTimeout(() => finishClick(), 10);
        } else if (continueRef.current) {
          setTimeout(() => continueClick(document.activeElement?.id), 10);
        }
      }
    },
    [api, continueClick, finishClick]
  );

  useEffect(() => {
    document.addEventListener("keypress", listener);
    return () => {
      document.removeEventListener("keypress", listener);
    };
  }, [listener]);

  const continueRef = useRef(null);
  const finishRef = useRef(null);

  const [numPages, setNumPages] = useState(1);
  const [range, setRange] = useState([1, 2, 3, 4]);

  const getNumPages = useCallback(() => {
    let a = +api?.d?.Pages?.length || 0 / 4;
    if (a < 1) return 1;
    return Math.ceil(a);
  }, [api]);

  useEffect(() => {
    const newPages = getNumPages();

    if (newPages > 4) {
      let _pageNum = currentPage > 3 ? currentPage : 3;
      if (_pageNum > newPages - 2) _pageNum = newPages - 1;
      setRange(_.range(_pageNum - 2, _pageNum + 2));
    } else {
      setRange(_.range(1, newPages + 1));
    }
    if (newPages !== numPages) setNumPages(newPages);
  }, [getNumPages, numPages, currentPage]);

  const handleArrowClick = useCallback(
    (direction) => {
      let newOffset = currentPage;

      switch (direction) {
        case "right":
          if (currentPage < numPages) setCurrentPage(currentPage + 1);
          newOffset = +currentPage + 1;
          break;
        case "left":
          if (currentPage > 1) setCurrentPage(currentPage - 1);
          newOffset = +currentPage - 1;
          break;
        default:
          break;
      }

      if (newOffset < 0) return (newOffset = 0);
      if (newOffset / 4 >= getNumPages(api?.d?.Pages?.length || 0, 4))
        return (newOffset = currentPage);
      setCurrentPage(newOffset);
    },
    [api, currentPage, getNumPages, numPages, setCurrentPage]
  );

  return (
    <BlurContainer>
      <Wrapper>
        <Heading mt="1rem" center>
          Total Price: {price}
        </Heading>
        <Flex
          mx={["1rem", "1rem", 0]}
          mt="0"
          justifyContent="space-between"
          alignItems="center"
        >
          <Flex flex={1} justifyContent="flex-start" alignItems="center">
            <Button
              iconName="faBan"
              onClick={() => setShowCancel(true)}
              color={theme.gray}
            >
              Cancel
            </Button>
          </Flex>

          <Flex flex={1} justifyContent="flex-end" alignItems="center">
            <Button
              color={theme.gray}
              iconName="faListUl"
              onClick={() => setShowDetails(true)}
            />

            {!api?.d?.IsExecutionComplete && (
              <Button
                ref={continueRef}
                disabled={running}
                onClick={continueClick}
                onSubmit={continueClick}
                color={theme.primary}
                iconName="faArrowRight"
                type="submit"
              >
                {running ? (
                  <Loading
                    inline
                    color1={theme.secondary}
                    color2={theme.success}
                  />
                ) : (
                  "Continue"
                )}
              </Button>
            )}

            {api?.d?.IsExecutionComplete ? (
              <Button
                ref={finishRef}
                autoFocus={api?.d?.IsExecutionComplete}
                disabled={isFinishing}
                onClick={finishClick}
                onSubmit={finishClick}
                iconName="faFlagCheckered"
                type="submit"
              >
                {isFinishing ? (
                  <Loading
                    inline
                    color1={theme.secondary}
                    color2={theme.success}
                  />
                ) : (
                  "Finish"
                )}
              </Button>
            ) : null}
          </Flex>
        </Flex>
        {constants.CONFIGURATOR_MODES.Default === configuratorMode && (
          <PagesWrapper my="1rem">
            <Button
              reverse
              size="small"
              color={theme.secondary}
              style={{
                opacity: !currentPage ? "0" : undefined,
                pointerEvents: !currentPage ? "none" : undefined,
              }}
              iconName="faAngleLeft"
              onClick={() => handleArrowClick("left")}
            />
            {Pages?.map((page, i) => {
              if (!range.includes(i + 1)) return null;
              const { Caption, IsVisible } = page || {};
              return IsVisible ? (
                <Button
                  size="small"
                  className={currentPage !== i ? "hide" : undefined}
                  reverse={currentPage !== i}
                  key={`Page|${i}`}
                  color={theme.secondary}
                  onClick={() => setCurrentPage(i)}
                >
                  {Caption}
                </Button>
              ) : null;
            })}
            <Button
              size="small"
              reverse
              style={{
                opacity:
                  currentPage + 1 >= Pages?.length || 0 ? "0" : undefined,
                pointerEvents:
                  currentPage + 1 >= Pages?.length || 0 ? "none" : undefined,
              }}
              disabled={autoContinueMode === 0}
              iconName="faAngleRight"
              color={theme.secondary}
              onClick={() => handleArrowClick("right")}
            />
          </PagesWrapper>
        )}
      </Wrapper>
    </BlurContainer>
  );
};

export default TopBar;
